import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from './Header';
import Logo from './Logo';
import LocaleSwitcher from './LocaleSwitcher';
import { getNavigation, switchNavigationApp } from '../../services/navigationService';
import type { NavigationApp, NavigationMenuItem, NavigationPayload } from '../../types/navigation.types';
import GlobalSearch from '../../pages/global-search/global-search';

type AppNavigationProps = {
    userName: string;
    onLogout: (event: MouseEvent<HTMLAnchorElement>) => void;
    isImpersonating?: boolean;
    isLoggingOut?: boolean;
    initialLogoUrl?: string | null;
};

const FALLBACK_NAVIGATION: NavigationPayload = {
    branding: {
        textColorClass: 'text-white',
        backgroundColor: null,
        fallbackBackgroundClass: 'bg-blue-900',
    },
    tenant: null,
    header: {
        dashboardUrl: '/dashboard',
        logoUrl: null,
        logoAlt: 'Logo',
        switchProjectUrl: null,
        currentTenantName: null,
        settingsUrl: null,
        logoutUrl: '/project-logout',
        profileUrl: null,
        userName: 'User',
        primaryProfileName: null,
        avatarUrl: 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png',
    },
    apps: [],
    currentApp: null,
    menuItems: [
        {
            id: 'home',
            name: 'Home',
            url: '/dashboard',
            icon: null,
            children: [],
        },
        {
            id: 'object-listing',
            name: 'Object Listing',
            url: '/custom-objects-page',
            icon: null,
            children: [],
        },
    ],
};

function Icon({ icon, className = '' }: { icon?: string | null; className?: string }) {
    if (!icon) {
        return null;
    }

    return <i className={`${icon} ${className}`.trim()} aria-hidden="true" />;
}

function resolvePathname(url?: string | null) {
    if (!url || url === '#') {
        return null;
    }

    try {
        return new URL(url, window.location.origin).pathname.replace(/\/$/, '') || '/';
    } catch {
        return url.startsWith('/') ? url.replace(/\/$/, '') || '/' : null;
    }
}

function matchesMenu(item: NavigationMenuItem, pathname: string): boolean {
    const normalizedPath = pathname.replace(/\/$/, '') || '/';
    const itemPath = resolvePathname(item.url);

    if (itemPath && (normalizedPath === itemPath || normalizedPath.startsWith(`${itemPath}/`))) {
        return true;
    }

    return item.children.some((child) => matchesMenu(child, normalizedPath));
}

function filterMenuTree(items: NavigationMenuItem[], query: string): NavigationMenuItem[] {
    const trimmedQuery = query.trim().toLowerCase();

    if (!trimmedQuery) {
        return items;
    }

    return items
        .map((item) => {
            const filteredChildren = filterMenuTree(item.children, trimmedQuery);
            const searchText = `${item.name} ${item.url || ''}`.toLowerCase();

            if (searchText.includes(trimmedQuery) || filteredChildren.length > 0) {
                return {
                    ...item,
                    children: filteredChildren,
                };
            }

            return null;
        })
        .filter((item): item is NavigationMenuItem => item !== null);
}

function SearchInput({
    value,
    onChange,
    placeholder,
}: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
}) {
    return (
        <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-500 focus-within:border-blue-400 focus-within:bg-white">
            <i className="fas fa-search text-xs" aria-hidden="true" />
            <input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                className="w-full bg-transparent text-sm text-gray-700 outline-none placeholder:text-gray-400"
            />
        </label>
    );
}

function AppSwitcherMenu({
    apps,
    currentApp,
    isOpen,
    onToggle,
    switchingAppId,
    onSwitch,
}: {
    apps: NavigationApp[];
    currentApp?: NavigationApp | null;
    isOpen: boolean;
    onToggle: () => void;
    switchingAppId: number | null;
    onSwitch: (app: NavigationApp) => void;
}) {
    if (apps.length === 0) {
        return null;
    }

    return (
        <div className="relative">
            <button
                type="button"
                onClick={onToggle}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
            >
                <Icon icon={currentApp?.icon} className="text-gray-600" />
                <span>{currentApp?.name || 'Select App'}</span>
                {switchingAppId !== null ? (
                    <i className="fas fa-spinner fa-spin text-blue-500" aria-hidden="true" />
                ) : (
                    <i className="fas fa-chevron-down text-[10px] text-gray-500" aria-hidden="true" />
                )}
            </button>

            {isOpen ? (
                <div className="absolute top-full left-0 z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white py-2 shadow-lg">
                    {apps.map((app) => {
                        const isCurrent = currentApp?.id === app.id;
                        const isSwitching = switchingAppId === app.id;

                        return (
                            <button
                                key={app.id}
                                type="button"
                                onClick={() => onSwitch(app)}
                                className={[
                                    'flex w-full items-center gap-3 px-4 py-3 text-left transition',
                                    isCurrent ? 'border-l-2 border-l-blue-500 bg-blue-50' : 'hover:bg-gray-50',
                                ].join(' ')}
                            >
                                {app.icon ? (
                                    <Icon icon={app.icon} className="w-5 text-center" />
                                ) : (
                                    <span
                                        className="h-5 w-5 rounded-full"
                                        style={{ backgroundColor: app.color || '#94a3b8' }}
                                    />
                                )}

                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-medium text-gray-900">{app.name}</span>
                                    {app.description ? (
                                        <span className="block truncate text-xs text-gray-500">{app.description}</span>
                                    ) : null}
                                </span>

                                {isSwitching ? (
                                    <i className="fas fa-spinner fa-spin text-xs text-blue-500" aria-hidden="true" />
                                ) : isCurrent ? (
                                    <i className="fas fa-check text-xs text-blue-500" aria-hidden="true" />
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

function FlyoutMenu({
    item,
    currentPath,
    level = 0,
}: {
    item: NavigationMenuItem;
    currentPath: string;
    level?: number;
}) {
    const hasChildren = item.children.length > 0;
    const isNested = level > 0;
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const baseLinkClass = isNested
        ? 'flex items-center gap-3 rounded-md px-2 py-2 text-xs text-gray-700 transition hover:bg-gray-50'
        : 'flex h-10 items-center gap-2 rounded px-3 text-xs font-medium transition hover:bg-white/10';
    const activeClass = matchesMenu(item, currentPath) ? (isNested ? 'bg-blue-50 text-blue-700' : 'bg-white/10') : '';

    useEffect(() => {
        if (!hasChildren) {
            return;
        }

        const handleOutsideClick = (event: globalThis.MouseEvent) => {
            const target = event.target as Node;

            if (!containerRef.current?.contains(target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [hasChildren]);

    const handleMouseEnter = () => {
        setIsOpen(true);
    };

    const handleMouseLeave = (event: React.MouseEvent) => {
        const target = event.relatedTarget as Node;
        if (!containerRef.current?.contains(target)) {
            setIsOpen(false);
        }
    };

    if (!hasChildren) {
        const content = (
            <>
                {item.icon ? <Icon icon={item.icon} className={isNested ? 'text-gray-400' : ''} /> : null}

                <span className="whitespace-nowrap">{item.name}</span>
            </>
        );

        return (
            <NavLink
                to={item.url || '#'}
                className={({ isActive }) => `${baseLinkClass} ${isActive ? activeClass : ''}`.trim()}
            >
                {content}
            </NavLink>
        );
    }

    return (
        <div
            ref={containerRef}
            className={`group relative ${isNested ? '' : 'nav-menu-item'}`.trim()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button
                type="button"
                className={`${baseLinkClass} ${activeClass}`.trim()}
                onClick={(event) => {
                    event.preventDefault();
                    setIsOpen((prev) => !prev);
                }}
            >
                {item.icon ? <Icon icon={item.icon} className={isNested ? 'text-gray-400' : ''} /> : null}
                <span>{item.name}</span>
                <i
                    className={`fas ${isNested ? 'fa-chevron-right text-[10px]' : 'fa-chevron-down text-[10px]'} opacity-70`}
                    aria-hidden="true"
                />
            </button>

            <div
                className={[
                    'absolute z-50 min-w-56 rounded-lg border border-gray-200 bg-white p-2 text-gray-800 shadow-xl transition duration-150',
                    isOpen ? 'visible opacity-100' : 'invisible opacity-0 group-hover:visible group-hover:opacity-100',
                    isNested ? 'top-0 left-full ml-1' : 'top-full left-0 mt-0',
                ].join(' ')}
            >
                {item.children.map((child) => (
                    <FlyoutMenu key={child.id} item={child} currentPath={currentPath} level={level + 1} />
                ))}
            </div>
        </div>
    );
}

function OverflowMenuItem({ item }: { item: NavigationMenuItem }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasChildren = item.children.length > 0;

    const toggleExpanded = () => {
        setIsExpanded((current) => !current);
    };

    return (
        <li>
            <div className="rounded-lg bg-gray-50">
                <div className="flex items-center">
                    <NavLink
                        to={item.url || '#'}
                        className="flex flex-1 items-center gap-2 px-3 py-3 text-left text-xs text-gray-700 transition hover:bg-gray-100"
                        onClick={(event) => {
                            if (hasChildren) {
                                event.preventDefault();
                                event.stopPropagation();
                                toggleExpanded();
                            }
                        }}
                    >
                        <Icon icon={item.icon} className="text-blue-600" />
                        <span className="flex-1">{item.name}</span>
                    </NavLink>

                    {hasChildren ? (
                        <button type="button" onClick={toggleExpanded} className="px-3 text-gray-400">
                            <i
                                className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-[10px]`}
                                aria-hidden="true"
                            />
                        </button>
                    ) : null}
                </div>

                {hasChildren && isExpanded ? (
                    <ul className="space-y-1 border-l-2 border-gray-200 px-3 pb-3 pl-5">
                        {item.children.map((child) => (
                            <OverflowMenuItem key={child.id} item={child} />
                        ))}
                    </ul>
                ) : null}
            </div>
        </li>
    );
}

function MenuMeasure({ item }: { item: NavigationMenuItem }) {
    return (
        <div className="flex h-10 items-center gap-2 rounded px-3 text-xs font-medium whitespace-nowrap">
            <Icon icon={item.icon} />
            <span>{item.name}</span>
            {item.children.length > 0 ? <i className="fas fa-chevron-down text-[10px]" aria-hidden="true" /> : null}
        </div>
    );
}

export default function AppNavigation({
    userName,
    onLogout,
    isImpersonating = false,
    isLoggingOut = false,
    initialLogoUrl = null,
}: AppNavigationProps) {
    const location = useLocation();
    const [navigation, setNavigation] = useState<NavigationPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [isLauncherOpen, setIsLauncherOpen] = useState(false);
    const [isAppSwitcherOpen, setIsAppSwitcherOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isOverflowOpen, setIsOverflowOpen] = useState(false);
    const [appQuery, setAppQuery] = useState('');
    const [overflowQuery, setOverflowQuery] = useState('');
    const [switchingAppId, setSwitchingAppId] = useState<number | null>(null);
    const [visibleIds, setVisibleIds] = useState<string[]>([]);

    const navRowRef = useRef<HTMLElement | null>(null);
    const navAreaRef = useRef<HTMLDivElement | null>(null);
    const launcherRef = useRef<HTMLDivElement | null>(null);
    const overflowButtonRef = useRef<HTMLButtonElement | null>(null);
    const appLauncherMenuRef = useRef<HTMLDivElement | null>(null);
    const appSwitcherRef = useRef<HTMLDivElement | null>(null);
    const userMenuRef = useRef<HTMLDivElement | null>(null);
    const overflowMenuRef = useRef<HTMLDivElement | null>(null);
    const measureRefs = useRef<Record<string, HTMLDivElement | null>>({});

    useEffect(() => {
        let active = true;

        const loadNavigation = async () => {
            try {
                const payload = await getNavigation();

                if (active) {
                    setNavigation(payload);
                }
            } catch {
                if (active) {
                    setNavigation(FALLBACK_NAVIGATION);
                    toast.error('Failed to load navigation. Showing fallback links.');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        void loadNavigation();

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        setIsLauncherOpen(false);
        setIsAppSwitcherOpen(false);
        setIsUserMenuOpen(false);
        setIsOverflowOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handleOutsideClick = (event: globalThis.MouseEvent) => {
            const target = event.target as Node;

            if (!launcherRef.current?.contains(target) && !appLauncherMenuRef.current?.contains(target)) {
                setIsLauncherOpen(false);
            }

            if (!appSwitcherRef.current?.contains(target)) {
                setIsAppSwitcherOpen(false);
            }

            if (!userMenuRef.current?.contains(target)) {
                setIsUserMenuOpen(false);
            }

            if (!overflowButtonRef.current?.contains(target) && !overflowMenuRef.current?.contains(target)) {
                setIsOverflowOpen(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    const effectiveNavigation = navigation ?? FALLBACK_NAVIGATION;
    const headerData = effectiveNavigation.header;
    const menuItems = effectiveNavigation.menuItems;
    const currentPath = location.pathname.replace(/\/$/, '') || '/';
    const filteredApps = effectiveNavigation.apps.filter((app) => {
        if (!appQuery.trim()) {
            return true;
        }

        const haystack = `${app.name} ${app.description || ''}`.toLowerCase();
        return haystack.includes(appQuery.trim().toLowerCase());
    });
    const overflowItems = (menuItems || []).filter((item) => !visibleIds.includes(item.id));
    const filteredOverflowItems = filterMenuTree(overflowItems, overflowQuery);
    const visibleItems = (menuItems || []).filter((item) => visibleIds.includes(item.id));
    const headerStyle: CSSProperties = effectiveNavigation.branding.backgroundColor
        ? {
              backgroundColor: effectiveNavigation.branding.backgroundColor,
              color: effectiveNavigation.branding.textColorClass,
          }
        : {};
    const navRowClassName = [
        effectiveNavigation.branding.textColorClass,
        effectiveNavigation.branding.fallbackBackgroundClass || '',
    ].join(' ');

    useLayoutEffect(() => {
        if (!menuItems.length || !navAreaRef.current) {
            return;
        }

        const recalculate = () => {
            const navWidth = navAreaRef.current?.getBoundingClientRect().width ?? 0;
            const launcherWidth = launcherRef.current?.getBoundingClientRect().width ?? 0;
            const overflowWidth = overflowButtonRef.current?.getBoundingClientRect().width || 52;
            const widths = menuItems.map((item) => ({
                id: item.id,
                width: Math.ceil(measureRefs.current[item.id]?.getBoundingClientRect().width ?? 0) + 16,
            }));
            const baseAvailableWidth = Math.max(navWidth - launcherWidth - 60, 200);
            const totalWidth = widths.reduce((sum, item) => sum + item.width, 0);
            const availableWidth =
                totalWidth > baseAvailableWidth
                    ? Math.max(baseAvailableWidth - overflowWidth, 200)
                    : baseAvailableWidth;
            const nextVisibleIds: string[] = [];
            let runningWidth = 0;

            widths.forEach((item) => {
                if (runningWidth + item.width <= availableWidth) {
                    runningWidth += item.width;
                    nextVisibleIds.push(item.id);
                }
            });

            if (nextVisibleIds.length === 0 && menuItems[0]) {
                nextVisibleIds.push(menuItems[0].id);
            }

            setVisibleIds(nextVisibleIds);
        };

        recalculate();

        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recalculate) : null;

        if (resizeObserver) {
            if (navAreaRef.current) {
                resizeObserver.observe(navAreaRef.current);
            }

            if (navRowRef.current) {
                resizeObserver.observe(navRowRef.current);
            }
        }

        window.addEventListener('resize', recalculate);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', recalculate);
        };
    }, [menuItems]);

    const handleSwitchApp = async (app: NavigationApp) => {
        if (switchingAppId === app.id || effectiveNavigation.currentApp?.id === app.id) {
            setIsLauncherOpen(false);
            setIsAppSwitcherOpen(false);
            return;
        }

        try {
            setSwitchingAppId(app.id);
            await switchNavigationApp(app.id);
            window.location.reload();
        } catch {
            toast.error('Failed to switch application.');
        } finally {
            setSwitchingAppId(null);
        }
    };

    const resolvedUserName = headerData.userName || userName;
    const profileLabel = headerData.primaryProfileName ? `(${headerData.primaryProfileName})` : '';

    return (
        <>
            <Header
                logo={
                    <Logo
                        to={headerData.dashboardUrl}
                        src={headerData.logoUrl || initialLogoUrl || undefined}
                        alt={headerData.logoAlt || 'Logo'}
                        imgClassName="h-9 w-auto"
                        width={140}
                    />
                }
                search={<GlobalSearch />}
                actions={
                    <>
                        {headerData.switchProjectUrl ? (
                            <a
                                href={headerData.switchProjectUrl}
                                className="flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-600 transition hover:bg-blue-200"
                            >
                                <i className="fas fa-exchange-alt text-[10px]" aria-hidden="true" />
                                <span>{headerData.currentTenantName || '-'}</span>
                            </a>
                        ) : null}

                        <div ref={appSwitcherRef}>
                            <AppSwitcherMenu
                                apps={effectiveNavigation.apps}
                                currentApp={effectiveNavigation.currentApp}
                                isOpen={isAppSwitcherOpen}
                                onToggle={() => setIsAppSwitcherOpen((current) => !current)}
                                switchingAppId={switchingAppId}
                                onSwitch={(app) => void handleSwitchApp(app)}
                            />
                        </div>

                        <LocaleSwitcher />

                        {headerData.settingsUrl ? (
                            <a
                                href={headerData.settingsUrl}
                                title="Settings"
                                className="rounded-full bg-white p-2 text-gray-700 transition hover:bg-gray-200"
                            >
                                <i className="fa fa-cog text-xl" aria-hidden="true" />
                            </a>
                        ) : null}

                        <div ref={userMenuRef} className="relative">
                            <button
                                type="button"
                                onClick={() => setIsUserMenuOpen((current) => !current)}
                                className="flex items-center gap-2"
                            >
                                <span className="hidden text-right text-xs font-medium lg:inline">
                                    <span className="block">Welcome! {resolvedUserName}</span>
                                    <span className="text-[10px] text-gray-500">{profileLabel}</span>
                                </span>
                                <img
                                    src={headerData.avatarUrl || FALLBACK_NAVIGATION.header.avatarUrl || undefined}
                                    alt="User avatar"
                                    className="h-8 w-8 rounded-full object-cover"
                                />
                                <i className="fas fa-chevron-down hidden text-[10px] md:inline" aria-hidden="true" />
                            </button>

                            {isUserMenuOpen ? (
                                <div className="absolute top-full right-0 z-60 mt-2 w-52 rounded-md border border-gray-200 bg-white py-2 shadow-lg">
                                    <NavLink
                                        to={headerData.profileUrl || '#'}
                                        className="block px-3 py-2 text-xs text-gray-700 hover:bg-gray-100"
                                    >
                                        <span className="block font-medium">{resolvedUserName}</span>
                                        <span className="text-[10px] text-gray-500">{profileLabel}</span>
                                    </NavLink>
                                    <a
                                        href={headerData.logoutUrl}
                                        onClick={isLoggingOut ? (event) => event.preventDefault() : onLogout}
                                        aria-disabled={isLoggingOut}
                                        className={`block px-2 py-1 text-xs text-red-600 hover:bg-red-50 ${
                                            isLoggingOut ? 'cursor-not-allowed opacity-60' : ''
                                        }`}
                                    >
                                        <i
                                            className={`fa ${
                                                isLoggingOut
                                                    ? 'fa-spinner fa-spin'
                                                    : isImpersonating
                                                      ? 'fa-user-secret'
                                                      : 'fa-sign-out-alt'
                                            } mr-2`}
                                            aria-hidden="true"
                                        />
                                        {isLoggingOut
                                            ? isImpersonating
                                                ? 'Returning…'
                                                : 'Logging out…'
                                            : isImpersonating
                                              ? 'Return to my account'
                                              : 'Logout'}
                                    </a>
                                </div>
                            ) : null}
                        </div>
                    </>
                }
            />

            <header ref={navRowRef} className={navRowClassName} style={headerStyle}>
                <div className="flex w-full flex-wrap items-center justify-between px-2 py-0 md:px-6">
                    <div ref={navAreaRef} className="flex min-w-0 flex-1 items-center gap-2">
                        <div ref={launcherRef} className="relative shrink-0">
                            <button
                                id="appLauncherBtn"
                                type="button"
                                className="rounded p-2 transition hover:bg-white/10"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setIsLauncherOpen((current) => !current);
                                }}
                            >
                                <i className="fas fa-th text-lg" aria-hidden="true" />
                            </button>

                            {isLauncherOpen ? (
                                <div
                                    ref={appLauncherMenuRef}
                                    className="absolute top-full left-0 z-50 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-4 text-gray-800 shadow-lg"
                                >
                                    <div className="mb-3 flex items-center justify-between gap-3">
                                        <div>
                                            <h3 className="text-lg font-semibold">App Launcher</h3>
                                            <p className="text-xs text-gray-500">
                                                {effectiveNavigation.currentApp?.name || 'Select an application'}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className="text-xl text-gray-500 transition hover:text-gray-700"
                                            onClick={() => setIsLauncherOpen(false)}
                                        >
                                            &times;
                                        </button>
                                    </div>

                                    <SearchInput value={appQuery} onChange={setAppQuery} placeholder="Search apps..." />

                                    <ul className="mt-4 max-h-64 space-y-2 overflow-y-auto">
                                        {filteredApps.length > 0 ? (
                                            filteredApps.map((app) => {
                                                const isCurrent = effectiveNavigation.currentApp?.id === app.id;
                                                const isSwitching = switchingAppId === app.id;

                                                return (
                                                    <li key={app.id}>
                                                        <button
                                                            type="button"
                                                            className={[
                                                                'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition',
                                                                isCurrent
                                                                    ? 'border-blue-200 bg-blue-50'
                                                                    : 'border-transparent hover:bg-gray-50',
                                                            ].join(' ')}
                                                            onClick={() => void handleSwitchApp(app)}
                                                        >
                                                            {app.icon ? (
                                                                <Icon icon={app.icon} className="w-5 text-center" />
                                                            ) : (
                                                                <span
                                                                    className="h-5 w-5 rounded-full"
                                                                    style={{ backgroundColor: app.color || '#94a3b8' }}
                                                                />
                                                            )}

                                                            <span className="min-w-0 flex-1">
                                                                <span className="block truncate text-sm font-medium text-gray-900">
                                                                    {app.name}
                                                                </span>
                                                                {app.description ? (
                                                                    <span className="block truncate text-xs text-gray-500">
                                                                        {app.description}
                                                                    </span>
                                                                ) : null}
                                                            </span>

                                                            {isSwitching ? (
                                                                <i
                                                                    className="fas fa-spinner fa-spin text-xs text-blue-500"
                                                                    aria-hidden="true"
                                                                />
                                                            ) : isCurrent ? (
                                                                <i
                                                                    className="fas fa-check text-xs text-blue-500"
                                                                    aria-hidden="true"
                                                                />
                                                            ) : null}
                                                        </button>
                                                    </li>
                                                );
                                            })
                                        ) : (
                                            <li className="rounded p-3 text-center text-sm text-gray-500">
                                                No apps found.
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            ) : null}
                        </div>

                        {loading ? (
                            <div className="flex min-w-0 items-center gap-3 overflow-hidden">
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <div key={index} className="h-10 w-24 animate-pulse rounded bg-white/25" />
                                ))}
                            </div>
                        ) : (
                            <nav id="main-nav" className="flex min-w-0 items-center gap-1 text-sm font-medium">
                                {visibleItems.map((item) => (
                                    <FlyoutMenu key={item.id} item={item} currentPath={currentPath} />
                                ))}
                            </nav>
                        )}

                        {!loading && overflowItems.length > 0 ? (
                            <div className="relative shrink-0">
                                <button
                                    ref={overflowButtonRef}
                                    id="overflowMenuBtn"
                                    type="button"
                                    className="rounded p-2 transition hover:bg-white/10"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setIsOverflowOpen((current) => !current);
                                    }}
                                >
                                    <i className="fas fa-plus text-lg" aria-hidden="true" />
                                </button>

                                {isOverflowOpen ? (
                                    <div
                                        ref={overflowMenuRef}
                                        className="absolute top-full right-0 z-60 mt-2 w-80 rounded-lg border border-gray-200 bg-white p-4 text-gray-800 shadow-lg"
                                    >
                                        <div className="mb-2 flex items-center justify-between gap-3">
                                            <h3 className="text-lg font-semibold">More Menus</h3>
                                            <button
                                                type="button"
                                                className="text-xl text-gray-500 transition hover:text-gray-700"
                                                onClick={() => setIsOverflowOpen(false)}
                                            >
                                                &times;
                                            </button>
                                        </div>

                                        <SearchInput
                                            value={overflowQuery}
                                            onChange={setOverflowQuery}
                                            placeholder="Search menus..."
                                        />

                                        <ul className="mt-4 max-h-80 space-y-2 overflow-y-auto">
                                            {filteredOverflowItems.length > 0 ? (
                                                filteredOverflowItems.map((item) => (
                                                    <OverflowMenuItem key={item.id} item={item} />
                                                ))
                                            ) : (
                                                <li className="rounded p-3 text-center text-sm text-gray-500">
                                                    No items found matching your search.
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <button
                                ref={overflowButtonRef}
                                type="button"
                                className="pointer-events-none absolute opacity-0"
                                tabIndex={-1}
                                aria-hidden="true"
                            >
                                <i className="fas fa-plus text-lg" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <div className="pointer-events-none invisible fixed top-0 -left-[9999px]" aria-hidden="true">
                {menuItems.map((item) => (
                    <div
                        key={item.id}
                        ref={(element) => {
                            measureRefs.current[item.id] = element;
                        }}
                        className="inline-block"
                    >
                        <MenuMeasure item={item} />
                    </div>
                ))}
            </div>
        </>
    );
}
